import React, { useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Flex, Spin, Typography, theme } from 'antd';

import {
  GSC_OAUTH_MESSAGE_TYPE,
  SEARCH_PERFORMANCE_CONNECT_ROUTE,
  deriveGscCallbackOutcome,
  gscCallbackOutcomeToQuery,
  isGscOAuthPopupWindow,
  resolveGscOAuthRelayTarget,
  type GscOAuthMessage,
} from '~features/SearchPerformance';

// Google's OAuth redirect URI is registered as this route, so the browser
// passes through here twice:
//   1. Raw Google redirect (?code&state) — relayed byte-for-byte to
//      visibility, which exchanges the code server-side and 302s back here.
//      The code is single-use and expires in minutes: forward immediately and
//      never store or log it.
//   2. Visibility's return redirect (?gsc_selection_session_id&source_id, or
//      ?status=error) — handed to the page that opened the popup via
//      postMessage, or carried into the wizard when there is no opener
//      (popup blocked → same-tab fallback, or the route was opened directly).
//
// This route is intentionally public: the popup runs a fresh app instance with
// no hydrated auth, so a guarded route would bounce it to login.
function GscOAuthCallbackPage() {
  const { token } = theme.useToken();
  const { search } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const relayUrl = resolveGscOAuthRelayTarget(search);
    if (relayUrl) {
      window.location.replace(relayUrl);
      return;
    }

    // Every leg that did not relay reads the same way, and the boot path reads
    // it too (features/auth/keycloakAuth rewrites the query with this outcome
    // when it has to clear Google's params before keycloak init).
    const outcome = deriveGscCallbackOutcome(searchParams);

    if (isGscOAuthPopupWindow()) {
      const payload: GscOAuthMessage = { type: GSC_OAUTH_MESSAGE_TYPE, ...outcome };
      (window.opener as Window).postMessage(payload, window.location.origin);
      window.close();
      return;
    }

    // The connect route, not the dashboard: this leg carries the session and
    // source ids the wizard needs to reach the domain picker, and the dashboard
    // would drop them.
    navigate(`${SEARCH_PERFORMANCE_CONNECT_ROUTE}?${gscCallbackOutcomeToQuery(outcome).toString()}`, { replace: true });
  }, [search, searchParams, navigate]);

  return (
    <Flex vertical align="center" justify="center" gap={token.margin} style={{ minHeight: '100vh' }}>
      <Spin size="large" />
      <Typography.Text type="secondary">Finishing Google Search Console connection…</Typography.Text>
    </Flex>
  );
}

export default GscOAuthCallbackPage;
